/**
 * The property form — `docs/record-flyouts.md` §5, built from the field kit
 * in `24-record-flyouts/html/flyout-field-kit.html`.
 *
 * One form for a property that exists and one that does not. There used to be
 * two: the short one for creating was missing the zone's explanation and the
 * staging notes, and the long one for editing was missing the access notes, so
 * which fields a property had depended on where you opened it from.
 *
 * The address is the identity (§4.6): the server keeps a normalized key and a
 * geocode and refuses a second record of the same place — so an address typed
 * wrong here is not a typo, it is a duplicate waiting to happen. When the
 * server says so it hands back the id of the one on file, and the form offers
 * to open it rather than making somebody find it.
 *
 * **Type is a `ComboInput`, not the `SegmentedField` §5 asks for.** A
 * segmented control is for two or three answers; `property_type` is free text
 * and the data already holds six in daily use (Commercial, HOA, Institutional,
 * Municipal, Industrial, Residential), which at half a 620 sheet is six
 * two-letter slivers. The combo asks the data what is in use
 * (`/properties/distinct`), offers those, and makes a new one from what was
 * typed — which is what the column actually is.
 */
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '@rowted/shared'
import { Sheet } from '../sheet.js'
import { Band, Button } from '../primitives.js'
import {
  AddressFields, ComboInput, Field, FormGrid, FormSection, RecordLookup, SelectInput,
  TextArea, TextInput, Wide,
  type AddressValue,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { IconCheck, IconPin } from '../../frame/icons.js'

/** A contact, as the primary-contact lookup reads it. */
interface Person { id: string; name: string; sub?: string }

interface Zone {
  id: string
  name: string
  orgUnitId: string
}

export interface PropertyRecord {
  id: string
  version: number
  name: string
  addressLine1: string
  addressLine2: string | null
  city: string
  state: string
  postalCode: string
  propertyType: string | null
  zoneId?: string | null
  stagingNotes?: string | null
  accessNotes?: string | null
  latitude?: number | null
  longitude?: number | null
}

/** Everything the form edits, as the API names it. */
interface PropertyFields {
  name: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  propertyType: string
  zoneId: string
  stagingNotes: string
  accessNotes: string
  /*
   * Where it is, when a suggestion said so — plan §4.6 pairs the geocode with
   * the normalized address for the duplicate check. Never typed: these two
   * are only ever set by taking a row from the address menu.
   */
  latitude: number | null
  longitude: number | null
}

const EMPTY: PropertyFields = {
  name: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '',
  propertyType: '', zoneId: '', stagingNotes: '', accessNotes: '',
  latitude: null, longitude: null,
}

/**
 * What a second property in the same place keeps.
 *
 * Adding five units of the same complex means five records one street number
 * apart, in the same town, the same zone and with the same gate code — so the
 * town, the zone and the type stay and the rest clears.
 */
const carryOver = (from: PropertyFields): PropertyFields => ({
  ...EMPTY,
  city: from.city,
  state: from.state,
  postalCode: from.postalCode,
  zoneId: from.zoneId,
  propertyType: from.propertyType,
})

/** An address to start a new property from — a suggestion somebody took elsewhere (Activity's *Attached to*). */
export interface StartingAddress {
  line1: string
  city: string
  state: string
  postalCode: string
  latitude: number | null
  longitude: number | null
}

const toForm = (record: PropertyRecord | null, initialName?: string, initialAddress?: StartingAddress | null): PropertyFields => record
  ? {
    name: record.name,
    addressLine1: record.addressLine1,
    addressLine2: record.addressLine2 ?? '',
    city: record.city,
    state: record.state,
    postalCode: record.postalCode,
    propertyType: record.propertyType ?? '',
    zoneId: record.zoneId ?? '',
    stagingNotes: record.stagingNotes ?? '',
    accessNotes: record.accessNotes ?? '',
    latitude: record.latitude ?? null,
    longitude: record.longitude ?? null,
  }
  : {
    ...EMPTY, name: initialName?.trim() ?? '',
    ...(initialAddress ? {
      addressLine1: initialAddress.line1, city: initialAddress.city, state: initialAddress.state,
      postalCode: initialAddress.postalCode, latitude: initialAddress.latitude, longitude: initialAddress.longitude,
    } : {}),
  }

export function PropertyForm({
  open, onClose, existing, orgUnitId, initialName, initialAddress, layer, onSaved, onOpenExisting,
}: {
  open: boolean
  onClose: () => void
  /** Present when editing; absent when adding. */
  existing?: PropertyRecord | null
  /** The branch a new property is filed under. Ignored when editing. */
  orgUnitId?: string
  /**
   * What to start the name as, when this was opened from a lookup somebody
   * had already typed into. Carrying the typed text through is the whole
   * point of creating from the field rather than from a different screen.
   */
  initialName?: string
  /**
   * The address to start from, when it was found somewhere else first —
   * Activity's *Attached to* finds it through Google and offers to make the
   * property (Aaron, Oct 24, note 1.1.4), so nobody types it twice.
   */
  initialAddress?: StartingAddress | null
  /** Raised when this form was opened from another — see `SheetProps.layer`. */
  layer?: number
  /** `again` is Save & add another: stay open, keep what the next one shares. */
  onSaved: (propertyId: string, again?: boolean) => void
  /** The id the server hands back when this place is already on file. */
  onOpenExisting?: (propertyId: string) => void
}) {
  const [form, setForm] = useState<PropertyFields>(() => toForm(existing ?? null, initialName, initialAddress))
  const [saved, setSaved] = useState<PropertyFields>(() => toForm(existing ?? null, initialName, initialAddress))
  const [zones, setZones] = useState<Zone[]>([])
  const [types, setTypes] = useState<string[]>([])
  const [primary, setPrimary] = useState<Person | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [clash, setClash] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [carried, setCarried] = useState(false)

  useEffect(() => {
    if (!open) return
    const fresh = toForm(existing ?? null, initialName, initialAddress)
    setForm(fresh)
    setSaved(fresh)
    setProblem(null)
    setClash(null)
    setErrors({})
    setCarried(false)
    setPrimary(null)

    void request<{ items: Zone[] }>('/properties/zones')
      .then((result) => setZones(result.items))
      .catch(() => setZones([]))
    void request<{ values: { value: string }[] }>('/properties/distinct?field=propertyType')
      .then((result) => setTypes(result.values.map((row) => row.value).filter(Boolean)))
      .catch(() => setTypes([]))
  }, [open, existing, initialName, initialAddress])

  const set = (part: Partial<PropertyFields>) => {
    setForm((current) => ({ ...current, ...part }))
    setErrors((current) => {
      const keys = Object.keys(part)
      return Object.fromEntries(Object.entries(current).filter(([key]) => !keys.includes(key)))
    })
  }

  const changes = useMemo(
    () => (Object.keys(form) as (keyof PropertyFields)[])
      .filter((key) => form[key] !== saved[key]).length,
    [form, saved])

  const address: AddressValue = {
    line1: form.addressLine1,
    line2: form.addressLine2,
    city: form.city,
    state: form.state,
    postalCode: form.postalCode,
    latitude: form.latitude,
    longitude: form.longitude,
  }

  /** The type combo's rows, drawn from what the data already holds. */
  const searchTypes = useMemo(() => (query: string) => {
    const wanted = query.trim().toLowerCase()
    return types
      .filter((one) => one.toLowerCase().includes(wanted))
      .slice(0, 8)
      .map((one) => ({ id: one, name: one }))
  }, [types])

  /**
   * What a property is called when nobody said.
   *
   * The name is required — a property with no name is a row nobody can pick
   * out of a list — but making somebody *type* one for 41 Grantley Rd is
   * asking them to retype the address they just typed. So it fills itself in
   * from the address, and says it did: "41 Grantley Rd, Red Lion, PA" is not
   * the name somebody would choose, and the hint invites them to choose a
   * better one, but it is a real name and it is right.
   */
  const autoName = [
    form.addressLine1.trim(),
    form.city.trim(),
    form.state.trim(),
  ].filter(Boolean).join(', ')

  /*
   * What the green dots promise.
   *
   * State and ZIP are in the address key the server de-duplicates on and in
   * the geocode the map and the routing read, so a property without them is
   * exactly the record that turns up twice under two spellings. `AddressFields`
   * marks all four, so all four are checked — a dot over a field nothing
   * enforces is the form lying about its own rules.
   */
  function check(next: PropertyFields): Record<string, string> {
    const found: Record<string, string> = {}
    /* The name is not checked: it fills itself in from the address. */
    if (!next.addressLine1.trim()) found.addressLine1 = 'A property is an address'
    if (!next.city.trim()) found.city = 'Which town?'
    if (!next.state.trim()) found.state = 'Which state?'
    if (!next.postalCode.trim()) found.postalCode = 'The ZIP is part of the address'
    return found
  }

  async function save(again: boolean) {
    const found = check(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    setClash(null)
    try {
      const body = {
        name: form.name.trim() || autoName,
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
        propertyType: form.propertyType.trim() || null,
        zoneId: form.zoneId || null,
        stagingNotes: form.stagingNotes.trim() || null,
        accessNotes: form.accessNotes.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
      }

      if (existing) {
        await request(`/properties/${existing.id}`, {
          method: 'PATCH',
          body: { ...body, version: existing.version },
        })
        onSaved(existing.id)
        return
      }

      const created = await request<{ id: string }>('/properties', {
        method: 'POST',
        body: { ...body, orgUnitId },
      })

      /*
       * The primary contact, linked straight away.
       *
       * A property with nobody on it is a property whose quote has nowhere to
       * go, and "add the contact afterwards" is the step everybody skips. If
       * the link fails the property still exists and is still right, so this
       * does not throw — the Contacts card on the record is the second
       * chance.
       */
      if (primary) {
        await request(`/contacts/${primary.id}/links`, {
          method: 'POST',
          body: { propertyId: created.id, isPrimary: true },
        }).catch(() => undefined)
      }

      if (again) {
        /* Not the geocode: the next unit is a different place. */
        const next = carryOver(form)
        setForm(next)
        setSaved(next)
        setCarried(true)
        onSaved(created.id, true)
        return
      }
      onSaved(created.id)
    } catch (cause) {
      /* On an address clash the server hands back the existing property's id. */
      const onFile = cause instanceof ApiError
        ? (cause.details?.propertyId as string | undefined)
        : undefined
      if (onFile) setClash(onFile)
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const count = Object.keys(errors).length

  return (
    <Sheet
      open
      layer={layer}
      onClose={onClose}
      kind="property"
      title={existing ? (form.name || 'Property') : 'New property'}
      sub="Properties · the site of truth for a place"
      dirty={changes > 0}
      changeCount={changes}
      saveLabel={existing ? 'Save property' : 'Add property'}
      onSave={() => void save(false)}
      note={count > 0
        ? (
          <span className="text-danger">
            {count === 1 ? 'One field needs attention' : `${count} fields need attention`}
          </span>
        )
        : undefined}
      secondary={!existing
        ? (
          <Button className="h-[34px]" disabled={busy} onClick={() => void save(true)}>
            Save &amp; add another
          </Button>
        )
        : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy}
          onClick={() => void save(false)}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : existing ? 'Save property' : 'Add property'}
        </Button>
      )}
    >
      {/*
        * The clash band carries the way out. "Address already in use" with no
        * button is a dead end somebody solves by inventing a second spelling
        * of the same street.
        */}
      {problem && (
        <Band tone="amber">
          {problem}
          {clash && onOpenExisting && (
            <Button className="ml-3 h-[28px]" onClick={() => onOpenExisting(clash)}>
              Open the one on file
            </Button>
          )}
        </Band>
      )}

      {carried && (
        <Band tone="green">
          Added. The town, the zone and the type stayed for the next one.
        </Band>
      )}

      <FormSection title="The place" />
      <FormGrid>
        <Wide>
          <Field
            label="Name"
            optional
            error={errors.name}
            hint={form.name.trim()
              ? 'what a crew would call it'
              : autoName
                ? `left empty it will be called “${autoName}”`
                : 'left empty it takes the address'}
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={form.name}
                placeholder={autoName || 'Willow Bend — clubhouse'}
                onChange={(event) => set({ name: event.target.value })}
              />
            )}
          </Field>
        </Wide>

        <Wide>
          <AddressFields
            value={address}
            required
            errors={{
              line1: errors.addressLine1,
              city: errors.city,
              state: errors.state,
              postalCode: errors.postalCode,
            }}
            onChange={(next) => set({
              addressLine1: next.line1,
              addressLine2: next.line2,
              city: next.city,
              state: next.state,
              postalCode: next.postalCode,
              /*
               * Typing clears the geocode, taking a suggestion sets it. A
               * latitude left over from the address before the one now in
               * the box is worse than none: it would put the pin, and the
               * duplicate check, on the wrong street.
               */
              latitude: next.latitude ?? null,
              longitude: next.longitude ?? null,
            })}
          />
        </Wide>

        {!existing && (
          <Wide>
            {/*
              * Somebody to ring, from the start. A property with nobody on it
              * is a property whose quote has nowhere to go, and "link the
              * contact afterwards" is the step everybody skips. One contact,
              * marked primary — the rest is the record page's Contacts card,
              * which does roles and bill-to properly.
              */}
            <Field
              label="Primary contact"
              optional
              hint="who to ring about this place — more can be linked on the record"
            >
              {({ id, describedBy }) => (
                <RecordLookup
                  id={id}
                  describedBy={describedBy}
                  kind="contact"
                  label="Primary contact"
                  placeholder="Search contacts…"
                  orgUnitId={orgUnitId}
                  value={primary}
                  onPick={(picked) => setPrimary(picked as Person)}
                />
              )}
            </Field>
          </Wide>
        )}
      </FormGrid>

      <FormSection title="How it is worked" />
      <FormGrid>
        <Field label="Type" hint={carried ? 'kept from the last one' : undefined}>
          {({ id, describedBy }) => (
            <ComboInput
              id={id}
              describedBy={describedBy}
              label="Type"
              thing="type"
              placeholder="Commercial, HOA, home…"
              value={form.propertyType ? { id: form.propertyType, name: form.propertyType } : null}
              onPick={(picked) => set({ propertyType: picked.name })}
              search={searchTypes}
              /*
               * The value *is* the word, so there is nothing to create on the
               * server — picking it is creating it. The row still says
               * "Create", because from here that is what it does.
               */
              onCreate={(name) => Promise.resolve({ id: name, name })}
            />
          )}
        </Field>

        <Field
          label="Zone"
          hint="routing groups a crew’s day by zone, so this is what keeps them from crossing town twice"
        >
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              label="Zone"
              value={form.zoneId || null}
              placeholder="No zone"
              clearable
              clearLabel="No zone"
              onChange={(value) => set({ zoneId: value ?? '' })}
              options={zones.map((zone) => ({ value: zone.id, label: zone.name }))}
            />
          )}
        </Field>
      </FormGrid>

      {/*
        * One Access section rather than two scattered note boxes (§5). Both
        * are read at the gate, on a phone, by somebody who has never been
        * here — so they sit together and say who reads them.
        */}
      <FormSection title="Access" />
      <FormGrid>
        <Wide>
          <Field label="Access notes" hint="crews see this on the visit, on a phone, at the gate">
            {({ id, describedBy }) => (
              <TextArea
                id={id}
                describedBy={describedBy}
                rows={3}
                value={form.accessNotes}
                placeholder="North gate 42 in — no skid steer, mini-skid only."
                onChange={(event) => set({ accessNotes: event.target.value })}
              />
            )}
          </Field>
        </Wide>
        <Wide>
          <Field label="Staging notes" optional hint="where to put the truck and the pile">
            {({ id, describedBy }) => (
              <TextArea
                id={id}
                describedBy={describedBy}
                rows={3}
                value={form.stagingNotes}
                placeholder="Park on the north verge. Bin store is behind the gate."
                onChange={(event) => set({ stagingNotes: event.target.value })}
              />
            )}
          </Field>
        </Wide>
      </FormGrid>

      <p className="m-0 flex items-start gap-1.5 text-[12px] leading-[1.45] text-text-muted">
        <span className="mt-0.5 shrink-0 text-text-faint"><IconPin size={13} /></span>
        The address is the identity: measured areas, sitemaps and every job here hang off this
        record, and the same place cannot be added twice.
      </p>
    </Sheet>
  )
}
